import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiHelper } from '../lib/api';
import http from '../lib/interceptor';
import { useAuth } from '../contexts/AuthContext';

// Query keys for cache management
export const performanceKeys = {
    all: ['performance'],
    kpiSections: (orgId, cycle) => [...performanceKeys.all, 'kpi-sections', orgId, cycle],
    employeeSections: (employeeId, cycleName, cycleStartDate) => [...performanceKeys.all, 'employee-sections', employeeId, cycleName, cycleStartDate],
    pendingReviews: (orgId) => [...performanceKeys.all, 'pending-reviews', orgId],
    reviews: (orgId) => [...performanceKeys.all, 'reviews', orgId],
    history: (orgId) => [...performanceKeys.all, 'history', orgId],
    requests360: () => [...performanceKeys.all, '360-requests'],
    summary360: (id) => [...performanceKeys.all, '360-summary', id],
    pending360: () => [...performanceKeys.all, '360-pending'],
    team360: (cycleName) => [...performanceKeys.all, '360-team', cycleName],
    lookup360: (type, orgId, search) => [...performanceKeys.all, '360-lookup', type, orgId, search],
};

// ===== Get KPI Sections =====
// GET /api/pdp/kpi-sections/?org_id=...&cycle_name={name}&cycle_start_date={date}
export function useKPISections(orgId, cycleName, cycleStartDate) {
    return useQuery({
        queryKey: performanceKeys.kpiSections(orgId, cycleName),
        queryFn: async () => {
            const params = new URLSearchParams();
            if (orgId) params.append('org_id', orgId);
            if (cycleName) params.append('cycle_name', cycleName);
            if (cycleStartDate) params.append('cycle_start_date', cycleStartDate);
            return apiHelper.getResource(`/pdp/kpi-sections/?${params.toString()}`);
        },
        enabled: !!orgId && !!cycleName && !!cycleStartDate,
        retry: false,
    });
}

// ===== Create KPI Section =====
// POST /api/pdp/kpi-sections/
export function useCreateKPISection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => {
            return apiHelper.postResource(`/pdp/kpi-sections/`, data);
        },
        onSuccess: (_, { orgId, data }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(orgId, data.cycle_name)
            });
        },
    });
}

// ===== Update KPI Section (PUT — all items) =====
// PUT /api/pdp/kpi-sections/{id}/
export function useUpdateKPISection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return apiHelper.putResource(`/pdp/kpi-sections/${id}/`, data);
        },
        onSuccess: (_, { orgId, data }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(orgId, data.cycle_name)
            });
        },
    });
}

// ===== Patch KPI Section (PATCH — single item) =====
// PATCH /api/pdp/kpi-sections/{id}/
export function usePatchKPISection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return apiHelper.patchResource(`/pdp/kpi-sections/${id}/`, data);
        },
        onSuccess: (_, { orgId, data }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(orgId, data.cycle_name)
            });
        },
    });
}

// ===== Self Rate Section =====
// POST /api/pdp/kpi-sections/{id}/self_rate/
export function useSelfRateSection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sectionId, data }) => {
            return apiHelper.postResource(`/pdp/kpi-sections/${sectionId}/self_rate/`, data);
        },
        onSuccess: (_, { orgId, data }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(orgId, data.cycle_name)
            });
        },
    });
}

// ===== Submit All Sections =====
export function useSubmitAllSections() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => {
            return apiHelper.postResource(`/pdp/kpi-sections/submit_all/`, data);
        },
        onSuccess: (_, { data }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(data.org, data.cycle_name)
            });
        },
    });
}

// ===== Submit Single Section =====
// POST /api/pdp/kpi-sections/{section_id}/submit/?org_id=...
export function useSubmitSection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sectionId, orgId }) => {
            return apiHelper.postResource(`/pdp/kpi-sections/${sectionId}/submit/?org_id=${orgId}`, {});
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(orgId)
            });
        },
    });
}

// ===== Manager Rate Section =====
// POST /api/pdp/kpi-sections/{id}/manager_rate/
export function useManagerRateSection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sectionId, data }) =>
            apiHelper.postResource(`/pdp/kpi-sections/${sectionId}/manager_rate/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...performanceKeys.all, 'pending-manager-ratings'] });
        },
    });
}

// ===== Get Pending Manager Ratings (self-rated sections awaiting manager review) =====
// GET /api/pdp/kpi-sections/pending_manager_ratings/?org_id=&cycle_name=&cycle_start_date=
export function usePendingManagerRatings(orgId, cycleName, cycleStartDate) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [...performanceKeys.all, 'pending-manager-ratings', orgId, cycleName, cycleStartDate],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (orgId) params.append('org_id', orgId);
            if (cycleName) params.append('cycle_name', cycleName);
            if (cycleStartDate) params.append('cycle_start_date', cycleStartDate);
            return apiHelper.getResource(`/pdp/kpi-sections/pending_manager_ratings/?${params.toString()}`);
        },
        enabled: !!orgId && !!user?.employeeId,
    });
}

// ===== Get Pending Reviews (Manager) =====
// GET /api/pdp/kpi-sections/pending_reviews/
export function usePendingReviews(orgId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: performanceKeys.pendingReviews(orgId),
        queryFn: async () => {
            return apiHelper.getResource(`/pdp/kpi-sections/pending_reviews/`);
        },
        enabled: !!orgId && !!user?.employeeId,
    });
}

// =====  Review Section (Accept/Reject) =====
// POST /api/pdp/kpi-sections/{section_id}/review/
export function useReviewSection() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ sectionId, data }) => {
            return apiHelper.postResource(`/pdp/kpi-sections/${sectionId}/review/`, data);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.pendingReviews(orgId)
            });
            queryClient.invalidateQueries({
                queryKey: performanceKeys.kpiSections(orgId)
            });
        },
    });
}

// ===== Create Review =====
// POST /api/pdp/reviews/?org_id=...
export function useCreateReview() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data, orgId }) => {
            return apiHelper.postResource(`/pdp/reviews/?org_id=${orgId}`, data);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.reviews(orgId)
            });
        },
    });
}

// ===== Evaluate Review (Self or Manager Assessment) =====
// POST /api/pdp/reviews/{review_id}/evaluate/?org_id=...
export function useEvaluateReview() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ reviewId, data, orgId }) => {
            return apiHelper.postResource(`/pdp/reviews/${reviewId}/evaluate/?org_id=${orgId}`, data);
        },
        onSuccess: (_, { orgId }) => {
            queryClient.invalidateQueries({
                queryKey: performanceKeys.reviews(orgId)
            });
            queryClient.invalidateQueries({
                queryKey: performanceKeys.history(orgId)
            });
        },
    });
}

// ===== Get Review History =====
// GET /api/pdp/reviews/history/?org_id=...
export function useReviewHistory(orgId) {
    const { user } = useAuth();
    return useQuery({
        queryKey: performanceKeys.history(orgId),
        queryFn: async () => {
            return apiHelper.getResource(`/pdp/reviews/history/?org_id=${orgId}`);
        },
        enabled: !!orgId && !!user?.employeeId,
    });
}

// ===== My KPI History (Employee sees their own sections) =====
// GET /api/pdp/kpi-sections/?cycle_name=&cycle_start_date= (both optional)
export function useMyKPIHistory(orgId, cycleName, cycleStartDate) {
    const { user } = useAuth();
    return useQuery({
        queryKey: [...performanceKeys.all, 'my-history', orgId, cycleName, cycleStartDate],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (cycleName) params.append('cycle_name', cycleName);
            if (cycleStartDate) params.append('cycle_start_date', cycleStartDate);
            return apiHelper.getResource(`/pdp/kpi-sections/?${params.toString()}`);
        },
        enabled: !!orgId && !!user?.employeeId,
    });
}

// ===== Employee KPI History (Manager views a specific supervisee) =====
// GET /api/pdp/kpi-sections/employee_sections/?employee_id=&cycle_name=&cycle_start_date=
export function useEmployeeKPIHistory(employeeId, cycleName, cycleStartDate) {
    return useQuery({
        queryKey: [...performanceKeys.all, 'emp-kpi-history', employeeId, cycleName, cycleStartDate],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (employeeId) params.append('employee_id', employeeId);
            if (cycleName) params.append('cycle_name', cycleName);
            if (cycleStartDate) params.append('cycle_start_date', cycleStartDate);
            return apiHelper.getResource(`/pdp/kpi-sections/employee_sections/?${params.toString()}`);
        },
        enabled: !!employeeId,
    });
}

// ===== Download Scorecard PDF =====
// GET /api/pdp/download/scorecard/?org_id=&cycle_name=&cycle_start_date=
export function useDownloadScorecard() {
    return useMutation({
        mutationFn: async ({ orgId, cycleName, cycleStartDate }) => {
            const params = new URLSearchParams();
            if (orgId) params.append('org_id', orgId);
            if (cycleName) params.append('cycle_name', cycleName);
            if (cycleStartDate) params.append('cycle_start_date', cycleStartDate);

            const response = await http.get(
                `/pdp/download/scorecard/?${params.toString()}`,
                { responseType: 'blob' }
            );

            // Derive filename from Content-Disposition or fall back to default
            const disposition = response.headers['content-disposition'] || '';
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match ? match[1].replace(/['"]/g, '') : 'scorecard.pdf';

            const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        },
    });
}

// ===== 360 FEEDBACK HOOKS =====

// 'supervisors' | 'supervisees' | 'colleagues' | 'employees'
export function use360Lookup(type, orgId, search = '', excludeIds = []) {
    return useQuery({
        queryKey: performanceKeys.lookup360(type, orgId, search),
        queryFn: async () => {
            const params = new URLSearchParams({ org_id: orgId });
            if (search) params.append('search', search);
            if (excludeIds.length) params.append('exclude_ids', excludeIds.join(','));
            return apiHelper.getResource(`/pdp/360/lookup/${type}/?${params.toString()}`);
        },
        enabled: !!type && !!orgId && (type === 'supervisors' || search.length >= 2),
    });
}

// List subject's own 360 requests
export function useMy360Requests() {
    return useQuery({
        queryKey: performanceKeys.requests360(),
        queryFn: () => apiHelper.getResource(`/pdp/360/requests/`),
    });
}

// Summary of a specific request (subject view)
export function use360RequestSummary(id) {
    return useQuery({
        queryKey: performanceKeys.summary360(id),
        queryFn: () => apiHelper.getResource(`/pdp/360/requests/${id}/summary/`),
        enabled: !!id,
    });
}

// Create 360 request (draft)
export function useCreate360Request() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => apiHelper.postResource(`/pdp/360/requests/`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: performanceKeys.requests360() });
        },
    });
}

// Activate a 360 request — fires emails to all nominees
export function useActivate360Request() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) => apiHelper.postResource(`/pdp/360/requests/${id}/activate/`, {}),
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: performanceKeys.requests360() });
            queryClient.invalidateQueries({ queryKey: performanceKeys.summary360(id) });
        },
    });
}

// Add a nomination to an existing request
export function useAddNomination360() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ requestId, data }) =>
            apiHelper.postResource(`/pdp/360/requests/${requestId}/add_nomination/`, data),
        onSuccess: (_, { requestId }) => {
            queryClient.invalidateQueries({ queryKey: performanceKeys.requests360() });
            queryClient.invalidateQueries({ queryKey: performanceKeys.summary360(requestId) });
        },
    });
}

// Remove a nomination (draft only)
export function useRemoveNomination360() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ requestId, nominationId }) =>
            apiHelper.deleteResource(`/pdp/360/requests/${requestId}/remove_nomination/?nomination_id=${nominationId}`),
        onSuccess: (_, { requestId }) => {
            queryClient.invalidateQueries({ queryKey: performanceKeys.requests360() });
        },
    });
}

// Pending nominations — rater's inbox
export function usePending360Nominations() {
    return useQuery({
        queryKey: performanceKeys.pending360(),
        queryFn: () => apiHelper.getResource(`/pdp/360/requests/my_pending_nominations/`),
    });
}

// Team's 360 requests — manager view
export function useTeam360Requests(cycleName) {
    return useQuery({
        queryKey: performanceKeys.team360(cycleName),
        queryFn: async () => {
            const params = new URLSearchParams();
            if (cycleName) params.append('cycle_name', cycleName);
            return apiHelper.getResource(`/pdp/360/requests/team_requests/?${params.toString()}`);
        },
    });
}

// Save partial response (rater, can call multiple times)
export function useSave360Response() {
    return useMutation({
        mutationFn: ({ nominationId, data }) =>
            apiHelper.postResource(`/pdp/360/nominations/${nominationId}/save_response/`, data),
    });
}

// Submit
export function useSubmit360Response() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ nominationId, data }) =>
            apiHelper.postResource(`/pdp/360/nominations/${nominationId}/submit_response/`, data),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: performanceKeys.pending360() });
        },
    });
}
